"use strict";
/**
 *  BigNumber
 *
 *  A wrapper around the BN.js object. We use the BN.js library
 *  because it is used by elliptic, so it is required regardless.
 *
 */

import _classCallCheck from "/opt/buildhome/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/opt/buildhome/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _BN from "bn.js";
var BN = _BN.BN;
import { hexlify, isBytes, isHexString } from "@ethersproject/bytes";
import { Logger } from "@ethersproject/logger";
import { version } from "./_version";
var logger = new Logger(version);
var _constructorGuard = {};
var MAX_SAFE = 0x1fffffffffffff;
export function isBigNumberish(value) {
  return value != null && (BigNumber.isBigNumber(value) || typeof value === "number" && value % 1 === 0 || typeof value === "string" && !!value.match(/^-?[0-9]+$/) || isHexString(value) || typeof value === "bigint" || isBytes(value));
} // Only warn about passing 10 into radix once

var _warnedToStringRadix = false;
export var BigNumber = /*#__PURE__*/function () {
  function BigNumber(constructorGuard, hex) {
    _classCallCheck(this, BigNumber);

    logger.checkNew(this instanceof BigNumber ? this.constructor : void 0, BigNumber);

    if (constructorGuard !== _constructorGuard) {
      logger.throwError("cannot call constructor directly; use BigNumber.from", Logger.errors.UNSUPPORTED_OPERATION, {
        operation: "new (BigNumber)"
      });
    }

    this._hex = hex;
    this._isBigNumber = true;
    Object.freeze(this);
  }

  _createClass(BigNumber, [{
    key: "fromTwos",
    value: function fromTwos(value) {
      return toBigNumber(toBN(this).fromTwos(value));
    }
  }, {
    key: "toTwos",
    value: function toTwos(value) {
      return toBigNumber(toBN(this).toTwos(value));
    }
  }, {
    key: "abs",
    value: function abs() {
      if (this._hex[0] === "-") {
        return BigNumber.from(this._hex.substring(1));
      }

      return this;
    }
  }, {
    key: "add",
    value: function add(other) {
      return toBigNumber(toBN(this).add(toBN(other)));
    }
  }, {
    key: "sub",
    value: function sub(other) {
      return toBigNumber(toBN(this).sub(toBN(other)));
    }
  }, {
    key: "div",
    value: function div(other) {
      var o = BigNumber.from(other);

      if (o.isZero()) {
        throwFault("division by zero", "div");
      }

      return toBigNumber(toBN(this).div(toBN(other)));
    }
  }, {
    key: "mul",
    value: function mul(other) {
      return toBigNumber(toBN(this).mul(toBN(other)));
    }
  }, {
    key: "mod",
    value: function mod(other) {
      var value = toBN(other);

      if (value.isNeg()) {
        throwFault("cannot modulo negative values", "mod");
      }

      return toBigNumber(toBN(this).umod(value));
    }
  }, {
    key: "pow",
    value: function pow(other) {
      var value = toBN(other);

      if (value.isNeg()) {
        throwFault("cannot raise to negative values", "pow");
      }

      return toBigNumber(toBN(this).pow(value));
    }
  }, {
    key: "and",
    value: function and(other) {
      var value = toBN(other);

      if (this.isNegative() || value.isNeg()) {
        throwFault("cannot 'and' negative values", "and");
      }

      return toBigNumber(toBN(this).and(value));
    }
  }, {
    key: "or",
    value: function or(other) {
      var value = toBN(other);

      if (this.isNegative() || value.isNeg()) {
        throwFault("cannot 'or' negative values", "or");
      }

      return toBigNumber(toBN(this).or(value));
    }
  }, {
    key: "xor",
    value: function xor(other) {
      var value = toBN(other);

      if (this.isNegative() || value.isNeg()) {
        throwFault("cannot 'xor' negative values", "xor");
      }

      return toBigNumber(toBN(this).xor(value));
    }
  }, {
    key: "mask",
    value: function mask(value) {
      if (this.isNegative() || value < 0) {
        throwFault("cannot mask negative values", "mask");
      }

      return toBigNumber(toBN(this).maskn(value));
    }
  }, {
    key: "shl",
    value: function shl(value) {
      if (this.isNegative() || value < 0) {
        throwFault("cannot shift negative values", "shl");
      }

      return toBigNumber(toBN(this).shln(value));
    }
  }, {
    key: "shr",
    value: function shr(value) {
      if (this.isNegative() || value < 0) {
        throwFault("cannot shift negative values", "shr");
      }

      return toBigNumber(toBN(this).shrn(value));
    }
  }, {
    key: "eq",
    value: function eq(other) {
      return toBN(this).eq(toBN(other));
    }
  }, {
    key: "lt",
    value: function lt(other) {
      return toBN(this).lt(toBN(other));
    }
  }, {
    key: "lte",
    value: function lte(other) {
      return toBN(this).lte(toBN(other));
    }
  }, {
    key: "gt",
    value: function gt(other) {
      return toBN(this).gt(toBN(other));
    }
  }, {
    key: "gte",
    value: function gte(other) {
      return toBN(this).gte(toBN(other));
    }
  }, {
    key: "isNegative",
    value: function isNegative() {
      return this._hex[0] === "-";
    }
  }, {
    key: "isZero",
    value: function isZero() {
      return toBN(this).isZero();
    }
  }, {
    key: "toNumber",
    value: function toNumber() {
      try {
        return toBN(this).toNumber();
      } catch (error) {
        throwFault("overflow", "toNumber", this.toString());
      }

      return null;
    }
  }, {
    key: "toString",
    value: function toString() {
      // Lots of people expect this, which we do not support, so check (See: #889)
      if (arguments.length > 0) {
        if (arguments[0] === 10) {
          if (!_warnedToStringRadix) {
            _warnedToStringRadix = true;
            logger.warn("BigNumber.toString does not accept any parameters; base-10 is assumed");
          }
        } else if (arguments[0] === 16) {
          logger.throwError("BigNumber.toString does not accept any parameters; use bigNumber.toHexString()", Logger.errors.UNEXPECTED_ARGUMENT, {});
        } else {
          logger.throwError("BigNumber.toString does not accept parameters", Logger.errors.UNEXPECTED_ARGUMENT, {});
        }
      }

      return toBN(this).toString(10);
    }
  }, {
    key: "toHexString",
    value: function toHexString() {
      return this._hex;
    }
  }, {
    key: "toJSON",
    value: function toJSON(key) {
      return {
        type: "BigNumber",
        hex: this.toHexString()
      };
    }
  }], [{
    key: "from",
    value: function from(value) {
      if (value instanceof BigNumber) {
        return value;
      }

      if (typeof value === "string") {
        if (value.match(/^-?0x[0-9a-f]+$/i)) {
          return new BigNumber(_constructorGuard, toHex(value));
        }

        if (value.match(/^-?[0-9]+$/)) {
          return new BigNumber(_constructorGuard, toHex(new BN(value)));
        }

        return logger.throwArgumentError("invalid BigNumber string", "value", value);
      }

      if (typeof value === "number") {
        if (value % 1) {
          throwFault("underflow", "BigNumber.from", value);
        }

        if (value >= MAX_SAFE || value <= -MAX_SAFE) {
          throwFault("overflow", "BigNumber.from", value);
        }

        return BigNumber.from(String(value));
      }

      var anyValue = value;

      if (typeof anyValue === "bigint") {
        return BigNumber.from(anyValue.toString());
      }

      if (isBytes(anyValue)) {
        return BigNumber.from(hexlify(anyValue));
      }

      if (anyValue) {
        // Hexable interface (takes piority)
        if (anyValue.toHexString) {
          var hex = anyValue.toHexString();

          if (typeof hex === "string") {
            return BigNumber.from(hex);
          }
        } else {
          // For now, handle legacy JSON-ified values (goes away in v6)
          var _hex = anyValue._hex; // New-form JSON

          if (_hex == null && anyValue.type === "BigNumber") {
            _hex = anyValue.hex;
          }

          if (typeof _hex === "string") {
            if (isHexString(_hex) || _hex[0] === "-" && isHexString(_hex.substring(1))) {
              return BigNumber.from(_hex);
            }
          }
        }
      }

      return logger.throwArgumentError("invalid BigNumber value", "value", value);
    }
  }, {
    key: "isBigNumber",
    value: function isBigNumber(value) {
      return !!(value && value._isBigNumber);
    }
  }]);

  return BigNumber;
}(); // Normalize the hex string

function toHex(value) {
  // For BN, call on the hex string
  if (typeof value !== "string") {
    return toHex(value.toString(16));
  } // If negative, prepend the negative sign to the normalized positive value


  if (value[0] === "-") {
    // Strip off the negative sign
    value = value.substring(1); // Cannot have mulitple negative signs (e.g. "--0x04")

    if (value[0] === "-") {
      logger.throwArgumentError("invalid hex", "value", value);
    } // Call toHex on the positive component


    value = toHex(value); // Do not allow "-0x00"

    if (value === "0x00") {
      return value;
    } // Negate the value


    return "-" + value;
  } // Add a "0x" prefix if missing


  if (value.substring(0, 2) !== "0x") {
    value = "0x" + value;
  } // Normalize zero


  if (value === "0x") {
    return "0x00";
  } // Make the string even length


  if (value.length % 2) {
    value = "0x0" + value.substring(2);
  } // Trim to smallest even-length string


  while (value.length > 4 && value.substring(0, 4) === "0x00") {
    value = "0x" + value.substring(4);
  }

  return value;
}

function toBigNumber(value) {
  return BigNumber.from(toHex(value));
}

function toBN(value) {
  var hex = BigNumber.from(value).toHexString();

  if (hex[0] === "-") {
    return new BN("-" + hex.substring(3), 16);
  }

  return new BN(hex.substring(2), 16);
}

function throwFault(fault, operation, value) {
  var params = {
    fault: fault,
    operation: operation
  };

  if (value != null) {
    params.value = value;
  }

  return logger.throwError(fault, Logger.errors.NUMERIC_FAULT, params);
} // value should have no prefix


export function _base36To16(value) {
  return new BN(value, 36).toString(16);
} // value should have no prefix

export function _base16To36(value) {
  return new BN(value, 16).toString(36);
}